export type PayloadLanguage = 'xml' | 'json';

// ponytail: length floor avoids flagging a stray inline tag/word (e.g. a
// bolded `<flag>`) as a payload block; raise if short false positives show up.
const MIN_LENGTH = 20;

/** Sniffs whether a chunk of text is, on its own, a raw XML or JSON blob.
 *  Used both to catch content markdown-to-jsx would otherwise mangle as HTML
 *  tag soup and to label it once fenced (see `wrapRawPayloadBlocks` and the
 *  `pre` override in `markdownOptions.tsx`). Tolerates one trailing '.'/'!'
 *  since agents often end a payload-only paragraph with a period. */
export function detectPayloadLanguage(text: string): PayloadLanguage | null {
  const trimmed = text.trim();
  if (trimmed.length < MIN_LENGTH) return null;
  const body = trimmed.replace(/[.!]+$/, '').trim();

  if (body.startsWith('<') && body.endsWith('>') && body.includes('</')) {
    return 'xml';
  }

  const looksLikeJson =
    (body.startsWith('{') && body.endsWith('}')) || (body.startsWith('[') && body.endsWith(']'));
  if (looksLikeJson) {
    try {
      JSON.parse(body);
      return 'json';
    } catch {
      return null;
    }
  }

  return null;
}

const FENCE_RE = /```[\s\S]*?```/g;

/** Promotes a standalone raw XML/JSON paragraph (not already inside a fenced
 *  code block) to a fenced ```xml/```json block, so it renders inside the
 *  boxed PayloadCodeBlock instead of as an unstyled (or, pre-fix, silently
 *  dropped) paragraph. Only acts on paragraphs that are ENTIRELY payload — a
 *  blob sharing a paragraph with prose is left alone; `disableParsingRawHTML`
 *  (see markdownOptions.tsx) is the fallback that keeps that mixed case
 *  visible as escaped inline text rather than losing it. */
export function wrapRawPayloadBlocks(text: string): string {
  if (!text) return text;
  const fences = text.match(FENCE_RE) ?? [];
  const segments = text.split(FENCE_RE);
  return segments.reduce((acc, segment, i) => {
    const rewritten = segment
      .split(/\n{2,}/)
      .map((paragraph) => {
        const language = detectPayloadLanguage(paragraph);
        if (!language) return paragraph;
        const body = paragraph
          .trim()
          .replace(/[.!]+$/, '')
          .trim();
        return '```' + language + '\n' + body + '\n```';
      })
      .join('\n\n');
    return acc + rewritten + (fences[i] ?? '');
  }, '');
}
